import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAVIGATION_ITEMS } from '../../constants';
import { useScroll } from '../../hooks/useScroll';
import { useLanguage } from '../../hooks/useLanguage';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { classNames } from '../../utils';
import { trackEvent } from '../../utils/analytics';
import logoDsw from '../../assets/logo_dsw2.png';
import '../../styles/components/Header.css';
import '../../styles/components/LanguageSelector.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { activeSection, scrollY, scrollToSection } = useScroll();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const isScrolled = scrollY > 50;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLanguageChange = lang => {
    if (lang === language) return;
    trackEvent('language_change', { from: language, to: lang });
    setLanguage(lang);
  };

  const handleAnchorClick = (e, item) => {
    e.preventDefault();
    const sectionId = item.href.replace('#', '');
    if (isHome) {
      scrollToSection(sectionId);
    } else {
      navigate(`/${item.href}`);
    }
    setIsMenuOpen(false);
  };

  const handleRouteClick = () => {
    setIsMenuOpen(false);
  };

  const handleLogoClick = e => {
    if (isHome) {
      e.preventDefault();
      scrollToSection('home');
    }
    setIsMenuOpen(false);
  };

  const isItemActive = item => {
    if (item.type === 'route') {
      return location.pathname === item.href ||
        (item.href === '/productos' &&
          location.pathname.startsWith('/productos'));
    }
    return isHome && activeSection === item.id;
  };

  const renderNavLink = item => {
    const className = classNames(
      'nav-link',
      isItemActive(item) && 'active'
    );

    if (item.type === 'route') {
      return (
        <Link
          key={item.id}
          to={item.href}
          className={className}
          onClick={handleRouteClick}
        >
          {t.navigation[item.id]}
        </Link>
      );
    }

    return (
      <a
        key={item.id}
        href={isHome ? item.href : `/${item.href}`}
        className={className}
        onClick={e => handleAnchorClick(e, item)}
      >
        {t.navigation[item.id]}
      </a>
    );
  };

  return (
    <header className={classNames('header', isScrolled && 'header-scrolled')}>
      <nav className='nav'>
        <Link to='/' className='nav-logo' onClick={handleLogoClick}>
          <div className='logo-icon'>
            <img
              src={logoDsw}
              alt='DSW Solutions Logo'
              className='logo-image'
            />
          </div>
          <span className='logo-text'></span>
        </Link>
        <div className={classNames('nav-links', isMenuOpen && 'active')}>
          {NAVIGATION_ITEMS.map(renderNavLink)}
        </div>
        <div className='language-selector'>
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className={classNames(
                'language-btn',
                language === lang.code && 'active'
              )}
              onClick={() => handleLanguageChange(lang.code)}
              aria-label={`Switch to ${lang.label}`}
            >
              <span className='language-flag'>{lang.flag}</span>
              {lang.label}
            </button>
          ))}
        </div>
        <button
          className='mobile-menu-btn'
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>
    </header>
  );
};

export default Header;
